<template>
  <div class="row">
    <div class="col-12">
      <page-header>
        <template #action-buttons>
          <b-button
            v-if="isEditor"
            variant="outline-primary"
            :disabled="!table.selectedRows || !table.selectedRows.length"
            size="sm"
            @click="deleteSelectedOrder"
          >
            {{ 'delete' | i18n }}
          </b-button>
          <b-button variant="primary" :disabled="!table.selectedRow" size="sm" @click="goToOrderDetails(table.selectedRow)">
            {{ (isEditor ? 'edit' : 'view') | i18n }}
          </b-button>
          <b-dropdown variant="outline-primary" right :disabled="!table.selectedRow" class="actions-dropdown" size="sm">
            <b-dropdown-item v-b-modal.modal-email-order>
              {{ 'email' | i18n }}
            </b-dropdown-item>
            <b-dropdown-item v-if="isEditor" @click="openCopyModal">
              {{ 'copy' | i18n }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </page-header>
      <div class="row">
        <div class="col-12 main-content">
          <!-- Table -->
          <div class="row">
            <div class="col-12">
              <filters v-model="table.filter" :filter-config="filterConfig" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <data-table
                id="orders-table"
                ref="table"
                v-model="table"
                :get-page="getPage"
                :per-page="perPage"
                :has-details-row="true"
                :selectable-rows="true"
                :editable-rows="true"
                @edit="goToOrderDetails"
              >
                <template #cell(offer)="row">
                  {{ row.item.offer === 1 ? 'Angebot' : 'Auftrag' }}
                </template>
                <template #cell(orderSumNumeric)="row">
                  {{ row.item.orderSumNumeric | formatPrice }}
                </template>
                <template #cell(createdDate)="row">
                  {{ row.item.createdDate | formatUnixDate('DD.MM.YY HH:mm') }}
                </template>
                <template #cell(dateOfDelivery)="row">
                  {{ row.item.dateOfDelivery | formatUnixDate('DD.MM.YY') }}
                </template>
                <template #cell(printRequested)="row">
                  <b-form-checkbox :checked="row.item.printRequested" disabled :inline="true" />
                </template>
                <template #row-details="row">
                  <order-table-details-row :row="row" />
                </template>
                <template #table-busy>
                  <div class="text-center">
                    <b-spinner class="align-middle" />
                    <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
                  </div>
                </template>
              </data-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <email-order-modal v-if="table.selectedRow" :order-number="table.selectedRow.orderDefaultNumber" @submit="emailOrder" />
    <copy-order v-if="table.selectedRow" :order-id="table.selectedRow.id" @copied="onOrderCopied" />
  </div>
</template>

<script>
import { OrderMixin, NotificationMixin, ScreenSizeMixin, AuthMixin, DeleteRowMixin } from '../../mixins';
import { mapActions } from 'vuex';
import EmailOrderModal from './email-order-modal';
import CopyOrder from './copy-order';
import appConfig from '../../app.config';
import collapseOpenIcon from '../../assets/icons/collapse-open.svg';
import collapseClosedIcon from '../../assets/icons/collapse-closed.svg';
import { SORT_ASC_TITLE, SORT_DESC_TITLE, NO_TABLE_DATA_TITLE } from '../../../../../constants';
import OrderTableDetailsRow from './table/order-table-details-row';
import Filters from '../table/filters';
import DataTable from '../table/data-table';
import PageHeader from '../structure/page-header';

export default {
  name: 'Orders',
  components: {
    PageHeader,
    DataTable,
    Filters,
    OrderTableDetailsRow,
    CopyOrder,
    EmailOrderModal
  },
  mixins: [OrderMixin, NotificationMixin, ScreenSizeMixin, AuthMixin, DeleteRowMixin],
  data: function() {
    return {
      orders: [],
      table: {
        rows: [],
        fields: [
          { key: 'check', label: '', thClass: 'check', tdClass: 'check' },
          {
            key: 'orderDefaultNumber',
            label: this.$translate('number_short'),
            sortable: true,
            thClass: 'order-number',
            tdClass: 'order-number',
            sortDirection: 'desc'
          },
          { key: 'offer', label: this.$translate('order_type_short'), thClass: 'offer', tdClass: 'offer' },
          { key: 'customerNumber', label: this.$translate('customer'), sortable: true, thClass: 'customer-number', tdClass: 'customer-number' },
          { key: 'name1', label: this.$translate('name'), sortable: true, thClass: 'name1', tdClass: 'name1' },
          { key: 'orderSumNumeric', label: this.$translate('sum'), thClass: ['order-sum', 'number-column'], tdClass: ['order-sum', 'number-column'] },
          { key: 'currency', label: this.$translate('currency'), thClass: 'currency', tdClass: 'currency' },
          { key: 'createdDate', label: this.$translate('order_date'), thClass: 'created-date', tdClass: 'created-date' },
          { key: 'information1', label: this.$translate('info'), sortable: true, thClass: 'information1', tdClass: 'information1' },
          { key: 'dateOfDelivery', label: this.$translate('date_of_delivery'), thClass: 'delivery-date', tdClass: 'delivery-date' },
          { key: 'orderOrigin', label: this.$translate('order_origin_short'), thClass: 'order-origin', tdClass: 'order-origin' },
          { key: 'printRequested', label: this.$translate('print'), thClass: 'print', tdClass: 'print' }
        ],
        selectedRow: null,
        sortDesc: true,
        sortBy: 'orderDefaultNumber',
        filter: {
          orderDefaultNumber: null,
          customerNumber: null,
          name1: null,
          information1: null,
          details: null,
          information2: null,
          sb: null,
          matchcode: null,
          address: null,
          orderOrigin: null
        }
      },
      filterConfig: {
        mainFilters: [
          { key: 'customerNumber', label: 'customer' },
          { key: 'address', label: 'customer_name' },
          { key: 'information1', label: 'info' }
        ],
        extraFilters: [
          { key: 'orderDefaultNumber', label: 'number_short' },
          { key: 'name1', label: 'name' },
          { key: 'details', label: 'ordering_information' },
          { key: 'information2', label: 'order_reference' },
          { key: 'matchcode', label: 'matchcode' },
          { key: 'orderOrigin', label: 'order_origin' },
          { key: 'sb', label: 'clerk' }
        ]
      },
      totalCount: null,
      perPage: 10,
      sortAscTitle: this.$translate(SORT_ASC_TITLE),
      sortDescTitle: this.$translate(SORT_DESC_TITLE),
      noTableDataTitle: this.$translate(NO_TABLE_DATA_TITLE),
      filtersShown: true,
      allDetailsShowing: false,
      isLoading: false,
      collapseOpenIcon,
      collapseClosedIcon,
      appConfig
    };
  },
  mounted() {
    if (this.isMediumScreen) {
      this.perPage = 15;
    } else if (this.isLargeScreen || this.isExtraLargeScreen) {
      this.perPage = 20;
    }
  },
  methods: {
    ...mapActions({ setSelectedOrder: 'order/setSelectedOrder' }),
    async getPage(params) {
      try {
        const response = await this.orderService.getOrdersPage(params);
        this.isLoading = false;
        const orders = response.orders.map(order => ({
          ...order,
          createdDate: order.createdDate,
          dateOfDelivery: order.dateOfDelivery,
          offer: order.offer,
          isSelected: false,
          _showDetails: this.allDetailsShowing
        }));
        return { totalCount: response.totalCount, data: orders };
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('orders'), message: err.message });
      }
    },
    async deleteSelectedOrder() {
      await this.deleteSelectedRows(
        {
          message: this.$translate('order_delete_confirmation_message'),
          rowDisplayValues: this.table.selectedRows.map(item => `${this.$translate('number_short')} ${item.orderDefaultNumber}`)
        },
        async () => {
          await this.orderService.deleteOrders({ ids: this.table.selectedRows.map(item => item.id).join(',') });
          this.table.selectedRow = null;
          await this.$refs.table.refresh();
        }
      );
    },
    async emailOrder(event) {
      try {
        await this.orderService.sendOrderEmail(this.table.selectedRow.id, event.email);
        this.$bvModal.hide('modal-email-order');
        this.showNotification({
          type: 'success',
          title: this.$translate('send_order_to_email_title'),
          message: this.$translate('order_sent_to_email_message')
        });
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('send_order_to_email_title'), message: err.error ? err.error.message : null });
      }
    },
    goToOrderDetails(order) {
      this.setSelectedOrder(order);
      this.$router.push({ name: 'order-details', params: { id: order.id } });
    },
    openCopyModal() {
      this.$root.$emit('bv::show::modal', 'modal-order-copy');
    },
    async onOrderCopied() {
      await this.$refs.table.refresh();
    },
    resetSelectedOrder() {
      this.table.selectedRow = null;
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep   .b-table-sticky-header {
  margin-bottom: 1rem;
  max-height: calc(100vh - 320px);
  min-height: calc(100vh - 320px);
  width: calc(100% - 3.5px);
}

@media (max-width: 768px) {
  .b-table-sticky-header {
    max-height: calc(100vh - 390px);
    min-height: calc(100vh - 390px);
  }
}

@media (max-width: 575px) {
  .b-table-sticky-header {
    max-height: calc(100vh - 500px);
    min-height: calc(100vh - 500px);
  }
}

::v-deep   .table {
  min-width: 1300px;
  margin-bottom: 0;
  width: calc(100% - 7px);

  .edit-row-button {
    display: inline-block;
    vertical-align: middle;
  }

  th,
  td {
    &.check {
      width: 10%;
    }

    &.order-number {
      width: 5%;
    }

    &.offer {
      width: 8%;
    }

    &.customer-number {
      width: 8%;
    }

    &.name1 {
      width: 13%;
    }

    &.order-sum {
      width: 9%;
    }

    &.currency {
      width: 8%;
    }

    &.created-date {
      width: 10%;
    }

    &.information1 {
      width: 10%;
    }

    &.delivery-date {
      width: 9%;
    }

    &.order-origin {
      width: 7%;
    }

    &.print {
      width: 3%;

      .custom-checkbox {
        margin-left: auto;
        margin-right: auto;
        display: block;
      }
    }
  }
}

::v-deep   .main-content {
  padding: 10px;
  // overflow: hidden;
}

.action-column {
  line-height: 36px;
}

.toggle-all-details {
  margin-left: 23px;
}

::v-deep   .action-buttons .dropdown-toggle {
  margin-right: 0;
}
</style>
